import { useEffect, useRef, useState } from 'react';
import {
  FlaskConical, Play, Loader2, Zap, Timer, AudioWaveform, Gauge, Radio, ChevronDown, ChevronRight,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const WS_URL = BACKEND_URL.replace('https://', 'wss://').replace('http://', 'ws://');

const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

const STATUS_STYLE = {
  ready: 'border-emerald-500/50 bg-emerald-500/10 text-emerald-300',
  not_configured: 'border-slate-600 bg-slate-800/60 text-slate-500',
};

const fmtMs = (v) => (v === null || v === undefined ? '—' : `${Math.round(v)} ms`);

const MetricCard = ({ label, value, suffix, testId, accent }) => (
  <div className={`rounded-lg border px-3 py-2 ${accent ? 'border-cyan-500/40 bg-cyan-500/10' : 'border-slate-700 bg-slate-900'}`} data-testid={testId}>
    <div className="text-[9px] uppercase tracking-widest text-slate-500">{label}</div>
    <div className={`font-mono text-sm font-bold ${accent ? 'text-cyan-300' : 'text-white'}`}>{value ?? '—'}{value !== null && value !== undefined && suffix ? suffix : ''}</div>
  </div>
);

const b64ToBytes = (b64) => {
  const bin = atob(b64);
  const arr = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) arr[i] = bin.charCodeAt(i);
  return arr;
};

const playPcmBytes = (ctxRef, bytes, sr = 16000) => {
  if (!bytes || !bytes.length) return;
  if (!ctxRef.current) ctxRef.current = new (window.AudioContext || window.webkitAudioContext)();
  const ctx = ctxRef.current;
  const pcm = new Int16Array(bytes.buffer, bytes.byteOffset, bytes.byteLength / 2);
  const buf = ctx.createBuffer(1, pcm.length, sr);
  const ch = buf.getChannelData(0);
  for (let i = 0; i < pcm.length; i++) ch[i] = pcm[i] / 32768;
  const src = ctx.createBufferSource();
  src.buffer = buf;
  src.connect(ctx.destination);
  src.start();
};

const AB_CRITERIA = ['Naturalidade', 'PT-PT', 'Pronúncia', 'Prosódia', 'Conforto'];

const CARTESIA_FROZEN = true; // STOP consumo Cartesia (créditos esgotados) — reativar só com aprovação explícita

const B32_LISTEN_CASES = [
  { id: 'key08', label: 'keynote · números (YoY, margem)' },
  { id: 'pan02', label: 'painel espontâneo' },
  { id: 'mtg05', label: 'reunião corporativa' },
  { id: 'qa02', label: 'Q&A' },
];

const B32Listen = () => (
  <div className="mt-4 rounded-xl border border-emerald-500/30 bg-slate-950/60 p-4 space-y-3" data-testid="b32-listen-section">
    <div className="text-emerald-300 text-xs font-bold tracking-widest uppercase">B3.2 · Escuta — Azure final vs Azure semantic incremental vs EL semantic</div>
    <p className="text-[10px] text-slate-500">
      Comparação ABERTA (não cega) com casos FORA da galeria de votação — sem spoiler da ronda cega.
      Foco: o Azure semantic incremental soa indistinguível do Azure final? (pausas, entoação, cortes, continuidade)
    </p>
    <div className="grid grid-cols-1 gap-2">
      {B32_LISTEN_CASES.map((c) => (
        <div key={c.id} className="rounded-lg border border-slate-700 bg-slate-900 p-3" data-testid={`b32-case-${c.id}`}>
          <div className="text-[10px] text-slate-400 mb-2 font-mono">{c.id} · {c.label}</div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
            {[['azure_final', 'Azure Raquel FINAL'], ['azure_semantic', 'Azure Raquel SEMANTIC INCR.'], ['el_semantic', 'ElevenLabs Marta semantic']].map(([tag, lbl]) => (
              <div key={tag} className="space-y-1">
                <div className="text-[9px] uppercase tracking-wider text-slate-500">{lbl}</div>
                <audio controls preload="none" src={`/tts_b32/${c.id}_${tag}.wav`} className="w-full h-8" data-testid={`b32-audio-${c.id}-${tag}`} />
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  </div>
);

const ABBlind = () => {
  const [abText, setAbText] = useState('O Millennium Estoril Open regressa a Cascais a 14 de outubro de 2026, com Carlos Alcaraz, Nuno Borges e um wild card para João Fonseca.');
  const [cartesiaVoice, setCartesiaVoice] = useState('beatriz-ptPT');
  const [busy, setBusy] = useState(false);
  const [abId, setAbId] = useState(null);
  const [samples, setSamples] = useState(null);
  const [scores, setScores] = useState({ A: {}, B: {} });
  const [pref, setPref] = useState('');
  const [reveal, setReveal] = useState(null);
  const [abError, setAbError] = useState('');
  const ctxRef = useRef(null);

  const wsAction = (payload, handler) => {
    const ws = new WebSocket(`${WS_URL}/api/ws/tts-lab`);
    ws.onopen = () => ws.send(JSON.stringify({ type: 'auth', token: localStorage.getItem('token') }));
    ws.onmessage = (ev) => {
      const msg = JSON.parse(ev.data);
      if (msg.type === 'auth_ok') { ws.send(JSON.stringify(payload)); return; }
      if (msg.type === 'error') { setAbError(msg.message); setBusy(false); ws.close(); return; }
      if (handler(msg)) ws.close();
    };
    ws.onerror = () => { setAbError('Erro de ligação WebSocket'); setBusy(false); };
  };

  const generateAb = () => {
    setBusy(true); setAbError(''); setSamples(null); setReveal(null);
    setScores({ A: {}, B: {} }); setPref('');
    wsAction({ action: 'generate_ab', text: abText, cartesia_voice: cartesiaVoice }, (msg) => {
      if (msg.type === 'ab_samples') {
        setAbId(msg.ab_id);
        const byKey = {};
        (msg.samples || []).forEach((s) => { byKey[s.key] = b64ToBytes(s.pcm_b64); });
        setSamples(byKey);
        setBusy(false);
        return true;
      }
      return false;
    });
  };

  const vote = () => {
    setBusy(true); setAbError('');
    wsAction({ action: 'ab_vote', ab_id: abId, scores, preference: pref }, (msg) => {
      if (msg.type === 'ab_reveal') { setReveal(msg.mapping); setBusy(false); return true; }
      return false;
    });
  };

  const scoresComplete = ['A', 'B'].every((k) => AB_CRITERIA.every((c) => scores[k][c])) && pref;

  return (
    <div className="mt-4 rounded-xl border border-amber-500/30 bg-slate-950/60 p-4 space-y-3" data-testid="ab-blind-section">
      <div className="flex items-center gap-2 text-amber-300 text-xs font-bold tracking-widest uppercase">A/B cego · Raquel vs Cartesia (B2)</div>
      {CARTESIA_FROZEN && (
        <div className="text-[11px] text-amber-400 border border-amber-500/40 bg-amber-500/10 rounded-md px-3 py-2" data-testid="ab-frozen-notice">
          Gerador A/B desativado: CARTESIA TESTING = FROZEN (créditos esgotados — HTTP 402). Este painel gera áudio novo na Cartesia e não é necessário para a votação. Usa a galeria "ABC cego" abaixo (áudios pré-gerados, custo zero).
        </div>
      )}
      <p className="text-[10px] text-slate-500">Duas vozes geram exatamente o mesmo texto. Ouve, pontua 1-5 e vota SEM saber qual é qual — o provider só é revelado depois do voto.</p>
      <textarea value={abText} onChange={(e) => setAbText(e.target.value)} rows={2} data-testid="ab-text-input"
        className="w-full bg-slate-950 border border-slate-700 rounded-md px-2 py-1.5 text-xs text-white resize-none" />
      <div className="flex items-center gap-3">
        <label className="text-[10px] text-slate-400 inline-flex items-center gap-2">
          Voz Cartesia
          <select value={cartesiaVoice} onChange={(e) => setCartesiaVoice(e.target.value)} data-testid="ab-cartesia-voice-select"
            className="bg-slate-950 border border-slate-700 rounded px-2 py-1 text-xs text-white">
            {['beatriz-ptPT', 'isabel-ptPT', 'tiago-ptPT', 'paulo-ptPT', 'diogo-ptPT'].map((v) => <option key={v} value={v}>{v}</option>)}
          </select>
        </label>
        <button onClick={generateAb} disabled={busy || !abText.trim() || CARTESIA_FROZEN} data-testid="ab-generate-btn"
          title={CARTESIA_FROZEN ? 'Cartesia sem créditos — gerador congelado' : undefined}
          className="inline-flex items-center gap-1.5 px-4 py-1.5 rounded-md bg-amber-600 hover:bg-amber-500 disabled:opacity-40 text-white text-xs font-bold">
          {busy && !samples ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Zap className="w-3.5 h-3.5" />} Gerar A/B
        </button>
      </div>
      {abError && <div className="text-[11px] text-red-400" data-testid="ab-error">{abError}</div>}
      {samples && (
        <div className="grid grid-cols-2 gap-3">
          {['A', 'B'].map((k) => (
            <div key={k} className="rounded-lg border border-slate-700 bg-slate-900 p-3 space-y-2" data-testid={`ab-panel-${k}`}>
              <div className="flex items-center justify-between">
                <span className="font-bold text-white text-sm">Áudio {k}</span>
                <button onClick={() => playPcmBytes(ctxRef, samples[k])} data-testid={`ab-play-${k}`}
                  className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md border border-slate-600 text-slate-300 hover:text-white text-[11px]">
                  <Play className="w-3 h-3" /> Ouvir
                </button>
              </div>
              {AB_CRITERIA.map((c) => (
                <label key={c} className="flex items-center justify-between text-[10px] text-slate-400 gap-2">
                  <span>{c}</span>
                  <select value={scores[k][c] || ''} data-testid={`ab-score-${k}-${c}`}
                    onChange={(e) => setScores((s) => ({ ...s, [k]: { ...s[k], [c]: parseInt(e.target.value, 10) } }))}
                    className="bg-slate-950 border border-slate-700 rounded px-1.5 py-0.5 text-xs text-white">
                    <option value="">—</option>
                    {[1, 2, 3, 4, 5].map((n) => <option key={n} value={n}>{n}</option>)}
                  </select>
                </label>
              ))}
              {reveal && (
                <div className="text-[10px] font-bold text-amber-300 border-t border-slate-700 pt-2" data-testid={`ab-reveal-${k}`}>
                  {reveal[k]?.provider === 'azure_current' ? 'Azure · ' : 'Cartesia · '}{reveal[k]?.voice}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
      {samples && !reveal && (
        <div className="flex items-center gap-3">
          <label className="text-[10px] text-slate-400 inline-flex items-center gap-2">
            Preferência global
            <select value={pref} onChange={(e) => setPref(e.target.value)} data-testid="ab-pref-select"
              className="bg-slate-950 border border-slate-700 rounded px-2 py-1 text-xs text-white">
              <option value="">—</option>
              <option value="A">Áudio A</option>
              <option value="B">Áudio B</option>
              <option value="tie">Empate</option>
            </select>
          </label>
          <button onClick={vote} disabled={busy || !scoresComplete} data-testid="ab-vote-btn"
            className="px-4 py-1.5 rounded-md bg-emerald-600 hover:bg-emerald-500 disabled:opacity-40 text-white text-xs font-bold">
            Votar e revelar
          </button>
        </div>
      )}
    </div>
  );
};

const ABCResults = ({ refreshKey }) => {
  const [data, setData] = useState(null);
  const [rErr, setRErr] = useState('');
  useEffect(() => {
    (async () => {
      try {
        const r = await fetch(`${API}/admin/tts-abc/results`, { headers: authHeaders() });
        const d = await r.json();
        if (!r.ok) throw new Error(d.detail || `HTTP ${r.status}`);
        setData(d); setRErr('');
      } catch (e) { setRErr(`Falha a carregar resultados agregados: ${e.message || e}`); }
    })();
  }, [refreshKey]);

  if (rErr) return <div className="text-[11px] text-red-400 border-t border-slate-800 pt-2" data-testid="abc-results-error">{rErr}</div>;
  if (!data || !data.groups) return null;

  const renderGroup = (g) => {
    const grp = data.groups[g];
    if (!grp) return null;
    const title = g === 'f' ? 'FEMALE RESULTS' : 'MALE RESULTS';
    if (!grp.all_cases_voted) {
      return (
        <p key={g} className="text-[10px] text-slate-500" data-testid={`abc-results-pending-${g}`}>
          {grp.label}: resultados agregados disponíveis após votar todos os casos ({grp.cases_voted}/{grp.cases_total} votados).
        </p>
      );
    }
    return (
      <div key={g} className="space-y-2" data-testid={`abc-results-section-${g}`}>
        <div className="text-cyan-300 text-xs font-bold tracking-widest uppercase">
          {title} · {grp.label} · {grp.total_votes} votos · {grp.cases_voted}/{grp.cases_total} casos
        </div>
        <div className="flex items-center gap-2 flex-wrap" data-testid={`abc-results-ranking-${g}`}>
          {grp.ranking.map((p) => (
            <div key={p.provider} data-testid={`abc-results-rank-${g}-${p.rank}`}
              className={`px-3 py-1.5 rounded-lg border text-xs font-bold ${p.rank === 1 ? 'border-emerald-500/60 text-emerald-300 bg-emerald-500/10' : 'border-slate-700 text-slate-300'}`}>
              #{p.rank} {p.label} · pref. {p.preference_wins} · média global {p.overall_mean ?? '—'}
            </div>
          ))}
        </div>
        <div className="grid grid-cols-1 gap-3">
          {grp.ranking.map((p) => (
            <div key={p.provider} className="rounded-lg border border-slate-700 bg-slate-900 p-3 space-y-2" data-testid={`abc-results-provider-${p.provider}`}>
              <div className="flex items-center justify-between">
                <span className="font-bold text-white text-xs">#{p.rank} {p.label}</span>
                <span className="text-[10px] text-slate-500">{p.cases_evaluated} casos</span>
              </div>
              <div className="text-[10px] text-slate-400">Preferência global: <span className="text-cyan-300 font-bold">{p.preference_wins}</span> vitórias</div>
              <table className="w-full text-[10px]">
                <thead>
                  <tr className="text-slate-500 text-left">
                    <th className="font-normal pb-1">Critério</th>
                    <th className="font-normal pb-1 text-right">Média</th>
                    <th className="font-normal pb-1 text-right">Mediana</th>
                    <th className="font-normal pb-1 text-right">Vit.</th>
                    <th className="font-normal pb-1 text-right">n</th>
                  </tr>
                </thead>
                <tbody>
                  {data.criteria.map((cr) => (
                    <tr key={cr} className="text-slate-300 border-t border-slate-800">
                      <td className="py-0.5">{cr}</td>
                      <td className="py-0.5 text-right font-mono">{p.criteria[cr]?.media ?? '—'}</td>
                      <td className="py-0.5 text-right font-mono">{p.criteria[cr]?.mediana ?? '—'}</td>
                      <td className="py-0.5 text-right font-mono">{p.criteria[cr]?.vitorias}</td>
                      <td className="py-0.5 text-right font-mono">{p.criteria[cr]?.n}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ))}
        </div>
      </div>
    );
  };

  return (
    <div className="border-t border-cyan-500/30 pt-3 space-y-4" data-testid="abc-results-root">
      {data.all_complete && (
        <div className="flex flex-wrap gap-2">
          {data.best_female_voice && (
            <div className="px-3 py-2 rounded-lg border border-emerald-500/60 bg-emerald-500/10 text-emerald-300 text-xs font-bold" data-testid="abc-best-female">
              BEST FEMALE VOICE · {data.best_female_voice}
            </div>
          )}
          {data.best_male_voice && (
            <div className="px-3 py-2 rounded-lg border border-emerald-500/60 bg-emerald-500/10 text-emerald-300 text-xs font-bold" data-testid="abc-best-male">
              BEST MALE VOICE · {data.best_male_voice}
            </div>
          )}
        </div>
      )}
      {['f', 'm'].map(renderGroup)}
    </div>
  );
};

const ABCGallery = () => {
  const [cases, setCases] = useState([]);
  const [idx, setIdx] = useState(0);
  const [scores, setScores] = useState({ A: {}, B: {}, C: {} });
  const [pref, setPref] = useState('');
  const [reveal, setReveal] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  const load = async () => {
    try {
      const r = await fetch(`${API}/admin/tts-abc/cases`, { headers: authHeaders() });
      const d = await r.json();
      setCases(d.cases || []);
    } catch { setErr('Falha a carregar casos A/B/C'); }
  };
  useEffect(() => { load(); }, []);

  if (!cases.length) return null;
  const c = cases[Math.min(idx, cases.length - 1)];
  const caseLetters = ['A', 'B', 'C'].filter((k) => c.files?.[k]);
  const complete = caseLetters.every((k) => AB_CRITERIA.every((cr) => scores[k][cr])) && pref;

  const goto = (i) => { setIdx(i); setScores({ A: {}, B: {}, C: {} }); setPref(''); setReveal(null); setErr(''); };

  const vote = async () => {
    setBusy(true); setErr('');
    try {
      const r = await fetch(`${API}/admin/tts-abc/vote`, {
        method: 'POST', headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ case_id: c.case_id, scores, preference: pref }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || 'erro');
      setReveal(d.reveal);
      setCases((cs) => cs.map((x) => (x.case_id === c.case_id ? { ...x, votes: (x.votes || 0) + 1 } : x)));
    } catch (e) { setErr(String(e.message || e)); }
    setBusy(false);
  };

  return (
    <div className="mt-4 rounded-xl border border-cyan-500/30 bg-slate-950/60 p-4 space-y-3" data-testid="abc-gallery-section">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="text-cyan-300 text-xs font-bold tracking-widest uppercase">ABC cego · Azure/Cartesia/ElevenLabs</div>
        <div className="flex items-center gap-1 flex-wrap">
          {['f', 'm'].map((g) => {
            const list = cases.filter((x) => (x.gender || 'f') === g);
            if (!list.length) return null;
            return (
              <div key={g} className="flex items-center gap-1 flex-wrap">
                <span className="text-[9px] uppercase tracking-wider text-slate-500 ml-1" data-testid={`abc-group-label-${g}`}>
                  {g === 'f' ? 'Fem' : 'Masc'}
                </span>
                {list.map((x) => {
                  const i = cases.indexOf(x);
                  return (
                    <button key={x.case_id} onClick={() => goto(i)} data-testid={`abc-case-${x.case_id}`}
                      className={`px-2 py-0.5 rounded text-[10px] font-mono border ${i === idx ? 'border-cyan-400 text-cyan-300' : 'border-slate-700 text-slate-500 hover:text-slate-300'}`}>
                      {x.case_id}{x.votes ? '✓' : ''}
                    </button>
                  );
                })}
              </div>
            );
          })}
        </div>
      </div>
      <p className="text-[10px] text-slate-500" data-testid="abc-current-case-info">
        Caso <span className="text-slate-300 font-mono">{c.case_id}</span> · {(c.gender || 'f') === 'm' ? 'vozes masculinas' : 'vozes femininas'} · perfil {c.profile} · {c.traits} — o mesmo conteúdo em {caseLetters.length} vozes (ordem aleatória). Pontua 1-5 e vota antes de revelar.
      </p>
      {err && <div className="text-[11px] text-red-400" data-testid="abc-error">{err}</div>}
      <div className={`grid gap-3 ${caseLetters.length === 2 ? 'grid-cols-2' : 'grid-cols-3'}`}>
        {caseLetters.map((k) => (
          <div key={k} className="rounded-lg border border-slate-700 bg-slate-900 p-3 space-y-2" data-testid={`abc-panel-${k}`}>
            <div className="font-bold text-white text-sm">Áudio {k}</div>
            <audio controls preload="none" src={c.files?.[k]} className="w-full h-8" data-testid={`abc-audio-${k}`} />
            {AB_CRITERIA.map((cr) => (
              <label key={cr} className="flex items-center justify-between text-[10px] text-slate-400 gap-2">
                <span>{cr}</span>
                <select value={scores[k][cr] || ''} data-testid={`abc-score-${k}-${cr}`}
                  onChange={(e) => setScores((s) => ({ ...s, [k]: { ...s[k], [cr]: parseInt(e.target.value, 10) } }))}
                  className="bg-slate-950 border border-slate-700 rounded px-1.5 py-0.5 text-xs text-white">
                  <option value="">—</option>
                  {[1, 2, 3, 4, 5].map((n) => <option key={n} value={n}>{n}</option>)}
                </select>
              </label>
            ))}
            {reveal && (
              <div className="text-[10px] font-bold text-cyan-300 border-t border-slate-700 pt-2" data-testid={`abc-reveal-${k}`}>
                {reveal[k]?.label}
              </div>
            )}
          </div>
        ))}
      </div>
      {!reveal && (
        <div className="flex items-center gap-3">
          <label className="text-[10px] text-slate-400 inline-flex items-center gap-2">
            Preferência global
            <select value={pref} onChange={(e) => setPref(e.target.value)} data-testid="abc-pref-select"
              className="bg-slate-950 border border-slate-700 rounded px-2 py-1 text-xs text-white">
              <option value="">—</option>
              {caseLetters.map((k) => <option key={k} value={k}>Áudio {k}</option>)}
            </select>
          </label>
          <button onClick={vote} disabled={busy || !complete} data-testid="abc-vote-btn"
            className="px-4 py-1.5 rounded-md bg-emerald-600 hover:bg-emerald-500 disabled:opacity-40 text-white text-xs font-bold">
            Votar e revelar
          </button>
          {idx < cases.length - 1 && (
            <button onClick={() => goto(idx + 1)} data-testid="abc-next-btn"
              className="px-3 py-1.5 rounded-md border border-slate-600 text-slate-300 hover:text-white text-xs">Próximo caso →</button>
          )}
        </div>
      )}
      {reveal && idx < cases.length - 1 && (
        <button onClick={() => goto(idx + 1)} data-testid="abc-next-after-vote-btn"
          className="px-3 py-1.5 rounded-md border border-cyan-500/50 text-cyan-300 hover:text-white text-xs">Próximo caso →</button>
      )}
      <ABCResults refreshKey={cases.reduce((a, x) => a + (x.votes || 0), 0)} />
    </div>
  );
};

export const TTSLab = () => {
  const [open, setOpen] = useState(false);
  const [providers, setProviders] = useState(null);
  const [providerId, setProviderId] = useState('azure_current');
  const [lang, setLang] = useState('pt');
  const [voice, setVoice] = useState('');
  const [streaming, setStreaming] = useState(true);
  const [textStreaming] = useState(false);
  const [bufferMs, setBufferMs] = useState(0);
  const [connMode, setConnMode] = useState('cold');
  const [cartesiaBuffer, setCartesiaBuffer] = useState('');
  const [text, setText] = useState('Bem-vindos à conferência de imprensa do Millennium Estoril Open, o torneio ATP 250 que regressa a Cascais.');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [recent, setRecent] = useState([]);
  const wsRef = useRef(null);
  const pcmChunksRef = useRef([]);
  const audioCtxRef = useRef(null);

  useEffect(() => {
    fetch(`${API}/admin/tts-lab/providers`, { headers: authHeaders() })
      .then((r) => (r.ok ? r.json() : null))
      .then(setProviders)
      .catch(() => setProviders(null));
    return () => { if (wsRef.current) wsRef.current.close(); };
  }, []);

  if (providers === null) return null; // não-admin ou lab desativado → invisível

  const provider = (providers.tts || []).find((p) => p.id === providerId) || {};
  const voiceOptions = (provider.voices || {})[lang] || [];

  const playPcm = () => {
    const chunks = pcmChunksRef.current;
    if (!chunks.length) return;
    const total = chunks.reduce((a, c) => a + c.length, 0);
    const pcm = new Int16Array(total / 2);
    let off = 0;
    chunks.forEach((c) => {
      const view = new Int16Array(c.buffer, c.byteOffset, c.byteLength / 2);
      pcm.set(view, off);
      off += view.length;
    });
    if (!audioCtxRef.current) audioCtxRef.current = new (window.AudioContext || window.webkitAudioContext)();
    const ctx = audioCtxRef.current;
    const sr = result?.sample_rate || 16000;
    const buf = ctx.createBuffer(1, pcm.length, sr);
    const ch = buf.getChannelData(0);
    for (let i = 0; i < pcm.length; i++) ch[i] = pcm[i] / 32768;
    const src = ctx.createBufferSource();
    src.buffer = buf;
    src.connect(ctx.destination);
    src.start();
  };

  const generate = () => {
    setBusy(true);
    setError('');
    setResult(null);
    pcmChunksRef.current = [];
    const ws = new WebSocket(`${WS_URL}/api/ws/tts-lab`);
    wsRef.current = ws;
    ws.onopen = () => ws.send(JSON.stringify({ type: 'auth', token: localStorage.getItem('token') }));
    ws.onmessage = (ev) => {
      const msg = JSON.parse(ev.data);
      if (msg.type === 'auth_ok') {
        const payload = { action: 'generate', provider: providerId, lang, voice: voice || null, text, streaming, buffer_ms: bufferMs, connection_mode: connMode };
        if (providerId === 'cartesia_sonic' && cartesiaBuffer !== '') payload.max_buffer_delay_ms = parseInt(cartesiaBuffer, 10);
        ws.send(JSON.stringify(payload));
      } else if (msg.type === 'audio_chunk' || msg.type === 'audio_full') {
        const bin = atob(msg.pcm_b64);
        const arr = new Uint8Array(bin.length);
        for (let i = 0; i < bin.length; i++) arr[i] = bin.charCodeAt(i);
        pcmChunksRef.current.push(arr);
      } else if (msg.type === 'result') {
        setResult({ ...msg.metrics, status: msg.status, error: msg.error, voice: msg.voice, region: msg.region, connection_state: msg.connection_state, sample_rate: 16000 });
        setRecent((cur) => [{ provider: msg.provider, voice: msg.voice, status: msg.status, connection_state: msg.connection_state, ...msg.metrics }, ...cur].slice(0, 8));
        setBusy(false);
        ws.close();
      } else if (msg.type === 'error') {
        setError(msg.message);
        setBusy(false);
        ws.close();
      }
    };
    ws.onerror = () => { setError('Erro de ligação WebSocket'); setBusy(false); };
    ws.onclose = (ev) => {
      if (ev.code === 4001 || ev.code === 4003 || ev.code === 4004) {
        setError(ev.reason || 'Acesso negado ao TTS Lab');
      }
      setBusy(false);
    };
  };

  return (
    <div className="max-w-lg mx-auto mt-6 rounded-2xl border border-violet-500/30 bg-slate-900/80 overflow-hidden" data-testid="tts-lab-section">
      <button onClick={() => setOpen((o) => !o)} className="w-full flex items-center gap-2 px-5 py-3.5 text-left hover:bg-slate-800/50 transition-colors" data-testid="tts-lab-toggle">
        {open ? <ChevronDown className="w-4 h-4 text-violet-400" /> : <ChevronRight className="w-4 h-4 text-violet-400" />}
        <FlaskConical className="w-4 h-4 text-violet-400" />
        <span className="font-['Manrope'] font-bold text-white text-sm">TTS LAB</span>
        <span className="text-[9px] font-bold px-2 py-0.5 rounded-full border border-violet-500/50 bg-violet-500/15 text-violet-300 tracking-widest">BENCHMARK · ADMIN</span>
      </button>
      {open && (
        <div className="px-5 pb-5 space-y-3">
          <p className="text-[10px] text-slate-500">Laboratório isolado — zero impacto nas sessões live. Delivery leg = WebSocket atual (ws_delivery).</p>
          <div className="grid grid-cols-2 gap-2">
            <label className="text-[10px] text-slate-400 space-y-1">
              <span>Provider</span>
              <select value={providerId} onChange={(e) => { setProviderId(e.target.value); setVoice(''); }} data-testid="tts-lab-provider-select"
                className="w-full bg-slate-950 border border-slate-700 rounded-md px-2 py-1.5 text-xs text-white">
                {(providers.tts || []).map((p) => (
                  <option key={p.id} value={p.id} disabled={p.status !== 'ready' || (CARTESIA_FROZEN && p.id.startsWith('cartesia'))}>
                    {p.label} {CARTESIA_FROZEN && p.id.startsWith('cartesia') ? '(FROZEN — sem créditos)' : (p.status !== 'ready' ? `(${p.status})` : '')}
                  </option>
                ))}
              </select>
            </label>
            <label className="text-[10px] text-slate-400 space-y-1">
              <span>Idioma</span>
              <select value={lang} onChange={(e) => { setLang(e.target.value); setVoice(''); }} data-testid="tts-lab-lang-select"
                className="w-full bg-slate-950 border border-slate-700 rounded-md px-2 py-1.5 text-xs text-white">
                {Object.keys(provider.voices || { pt: 1 }).map((l) => <option key={l} value={l}>{l.toUpperCase()}</option>)}
              </select>
            </label>
            <label className="text-[10px] text-slate-400 space-y-1">
              <span>Voz {provider.capabilities?.pt_pt === 'explicit' && lang === 'pt' ? '· pt-PT explícito ✓' : ''}</span>
              <select value={voice} onChange={(e) => setVoice(e.target.value)} data-testid="tts-lab-voice-select"
                className="w-full bg-slate-950 border border-slate-700 rounded-md px-2 py-1.5 text-xs text-white">
                <option value="">(default)</option>
                {voiceOptions.map((v) => <option key={v} value={v}>{v}</option>)}
              </select>
            </label>
            <label className="text-[10px] text-slate-400 space-y-1">
              <span>Buffer (ms)</span>
              <select value={bufferMs} onChange={(e) => setBufferMs(parseInt(e.target.value, 10))} data-testid="tts-lab-buffer-select"
                className="w-full bg-slate-950 border border-slate-700 rounded-md px-2 py-1.5 text-xs text-white">
                {[0, 150, 300, 500, 1000].map((b) => <option key={b} value={b}>{b}</option>)}
              </select>
            </label>
            <label className="text-[10px] text-slate-400 space-y-1">
              <span>Ligação (B1)</span>
              <select value={connMode} onChange={(e) => setConnMode(e.target.value)} data-testid="tts-lab-connmode-select"
                className="w-full bg-slate-950 border border-slate-700 rounded-md px-2 py-1.5 text-xs text-white">
                <option value="cold">cold — nova ligação por pedido</option>
                <option value="warm">warm — pool reutilizado</option>
              </select>
            </label>
            {providerId === 'cartesia_sonic' && (
              <label className="text-[10px] text-slate-400 space-y-1">
                <span>Cartesia max_buffer_delay (B2)</span>
                <select value={cartesiaBuffer} onChange={(e) => setCartesiaBuffer(e.target.value)} data-testid="tts-lab-cartesia-buffer-select"
                  className="w-full bg-slate-950 border border-slate-700 rounded-md px-2 py-1.5 text-xs text-white">
                  <option value="">default do servidor</option>
                  {[0, 150, 300, 500].map((b) => <option key={b} value={b}>{b} ms</option>)}
                </select>
              </label>
            )}
          </div>
          <div className="flex items-center gap-4 text-[10px] text-slate-400">
            <label className="inline-flex items-center gap-1.5 cursor-pointer">
              <input type="checkbox" checked={streaming} onChange={(e) => setStreaming(e.target.checked)} data-testid="tts-lab-streaming-toggle" />
              Streaming (chunks progressivos)
            </label>
            <label className="inline-flex items-center gap-1.5 opacity-40 cursor-not-allowed" title="Disponível nas fases B2+ (Cartesia/ElevenLabs)">
              <input type="checkbox" checked={textStreaming} disabled data-testid="tts-lab-text-streaming-toggle" />
              Text streaming (B2+)
            </label>
            <span className="ml-auto text-slate-500">Output: PCM16 · 16 kHz</span>
          </div>
          <textarea value={text} onChange={(e) => setText(e.target.value)} rows={2} data-testid="tts-lab-text-input"
            className="w-full bg-slate-950 border border-slate-700 rounded-md px-2 py-1.5 text-xs text-white resize-none" />
          <div className="flex items-center gap-2">
            <button onClick={generate} disabled={busy || !text.trim() || provider.status !== 'ready'} data-testid="tts-lab-generate-btn"
              className="inline-flex items-center gap-1.5 px-4 py-1.5 rounded-md bg-violet-600 hover:bg-violet-500 disabled:opacity-40 text-white text-xs font-bold">
              {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Zap className="w-3.5 h-3.5" />} Generate
            </button>
            {result && result.status === 'SUCCESS' && (
              <button onClick={playPcm} data-testid="tts-lab-play-btn"
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-slate-600 text-slate-300 hover:text-white text-xs">
                <Play className="w-3.5 h-3.5" /> Ouvir
              </button>
            )}
            {result && <span className={`text-[10px] font-bold ${result.status === 'SUCCESS' ? 'text-emerald-400' : 'text-red-400'}`} data-testid="tts-lab-status">{result.status}</span>}
          </div>
          {error && <div className="text-[11px] text-red-400" data-testid="tts-lab-error">{error}</div>}
          {result && (
            <div className="grid grid-cols-3 gap-2">
              <MetricCard label="TTFA (provider)" value={result.tts_ttfa_ms && Math.round(result.tts_ttfa_ms)} suffix=" ms" testId="tts-lab-ttfa" accent />
              <MetricCard label="Total TTS" value={result.tts_total_ms && Math.round(result.tts_total_ms)} suffix=" ms" testId="tts-lab-total" />
              <MetricCard label="Audio duration" value={result.audio_duration_ms && Math.round(result.audio_duration_ms)} suffix=" ms" testId="tts-lab-duration" />
              <MetricCard label="Realtime factor" value={result.realtime_factor} testId="tts-lab-rtf" />
              <MetricCard label="WS delivery" value={result.ws_delivery_ms && Math.round(result.ws_delivery_ms)} suffix=" ms" testId="tts-lab-ws-delivery" />
              <MetricCard label="Overhead nosso" value={result.our_overhead_ms && Math.round(result.our_overhead_ms)} suffix=" ms" testId="tts-lab-overhead" accent />
              <MetricCard label="Conn setup" value={result.connection_setup_ms !== null && result.connection_setup_ms !== undefined ? Math.round(result.connection_setup_ms) : null} suffix=" ms" testId="tts-lab-connsetup" />
              <MetricCard label="Estado ligação" value={result.connection_state} testId="tts-lab-connstate" accent />
            </div>
          )}
          {recent.length > 0 && (
            <div className="text-[10px] text-slate-500" data-testid="tts-lab-recent">
              <div className="uppercase tracking-widest mb-1 flex items-center gap-1"><Timer className="w-3 h-3" /> Últimas gerações</div>
              {recent.map((r, i) => (
                <div key={i} className="flex gap-3 font-mono py-0.5 border-t border-slate-800">
                  <span className="text-slate-400">{r.provider}</span>
                  <span>{fmtMs(r.tts_ttfa_ms)} TTFA</span>
                  <span>{fmtMs(r.our_overhead_ms)} overhead</span>
                  <span className="text-violet-300">{r.connection_state || '—'}</span>
                  <span className={r.status === 'SUCCESS' ? 'text-emerald-400' : 'text-red-400'}>{r.status}</span>
                </div>
              ))}
            </div>
          )}
          <ABBlind />
          <ABCGallery />
          <B32Listen />
        </div>
      )}
    </div>
  );
};
